import './click_handlers'
import './button_scrolling'
import mixpanel from 'mixpanel-browser';
import "../src/mixpanel_setup"

class Section {
    private readonly internalSectionId: string

    get sectionId(): string {
        if (this.parentSection) {
            if (this.parentSection.sectionId.length == 0) {
                return this.internalSectionId
            } else {
                return `${this.parentSection.sectionId}-subsection-${this.internalSectionId}`
            }
        } else {
            return this.internalSectionId
        }
    }

    get menuItemId(): string {
        return `${this.sectionId}-menu-item`
    }

    parentSection: Section | undefined
    
    isIntersecting: boolean = false
    yRelativeToRoot: number = 0
    
    childrenSections: Section[]

    constructor(sectionId: string, parentSection: Section | undefined = undefined, childrenSections: Section[] = []) {
        this.internalSectionId = sectionId
        this.parentSection = parentSection
        this.childrenSections  = childrenSections
        childrenSections.forEach(subsection => {
            subsection.parentSection = this
        })
    }
}

type JSONSection = {
    name: string
    children: JSONSection[]
}

const jsonStructureOfContent: JSONSection = {
    name: "", children: [
        {
            name: "App-Features-Overview", children: [
                { name: "Home-Tab", children: [] },
                { name: "Favorites-Tab", children: [] },
                { name: "Update-Tab", children: [] },
                { name: "You-Tab", children: [] },
                { name: "Cart-Tab", children: [] },
                { name: "Product-Details-Screen", children: [] },
            ]
        },
        { name: "What-Architecture-To-Pick", children: [] },
        { name: "Brief-Overview-of-VIPER", children: [] },
        { 
            name: "Overall-App-Architecture", children: [
                { name: "Screens", children: [] },
                { 
                    name: "Layers", children: [
                        { name: "UI-Layer", children: [] },
                        { name: "Networking-Service-Layer", children: [] },
                        { name: "Storage-Layer", children: [] },
                        { name: "Business-Logic-Layer", children: [] }
                    ] 
                },
                { name: "Dependencies", children: [] },
                { name: "Domain-Models", children: [] },
                { name: "Interactors-Presenters-Coordinators-Routers", children: [] }
            ] 
        },

        { 
            name: "Home-Tab-Screen", children: [
                { name: "Home-Tab-Features", children: [] },
                { name: "Home-Tab-Structure", children: [] },
                { 
                    name: "Home-Screen-Dependencies-Architecture", children: [
                        { name: "Home-VIPER-stack", children: [] },
                        { name: "SearchBar-VIPER-Stack", children: [] },
                        { name: "Product-List-VIPER-Stack", children: [] },
                        { name: "Hero-Promo-VIPER-Stacks", children: [] }
                    ] 
                },
                { name: "Data-Flow-In-Home-Screen", children: [] },
            ] 
        },

        { 
            name: "Product-Details-Screen", children: [
                { name: "Product-Details-Features", children: [] },
                { name: "Product-Details-Structure", children: [] },
                { name: "Product-Details-Dependencies-Architecture", children: [
                        { name: "Product-Details-VIPER-Stack", children: [] },
                        { name: "Product-Item-VIPER-Stack", children: [] },
                        { name: "Price-And-Quantity-VIPER-Stack", children: [] },
                        { name: "Reviews-VIPER-Stack", children: [] },
                        {name: "Product-List-VIPER-Stack", children: [] },
                    ] 
                },
                { name: "Data-Flow-In-Product-Details", children: [] },
            ] 
        },

        { name: "AB-Testing-Feature-Flagging", children: [] },
    ]
}

function mapJSONStructureOfContentToSectionViewModels(jsonStructureOfContent: JSONSection, parentSection: Section | undefined): Section {

    const section = new Section(jsonStructureOfContent.name, parentSection)

    const childrenSections = jsonStructureOfContent.children.map(childJSON => {
        return mapJSONStructureOfContentToSectionViewModels(childJSON, section)
    })

    section.childrenSections = childrenSections

    return section
}

const rootSection = mapJSONStructureOfContentToSectionViewModels(jsonStructureOfContent, undefined)

function getAllSectionAsArrayList(section: Section): Section[] {
    if (section.childrenSections.length == 0) {
        return [section]
    } else {
        let childrenSections: Section[] = []

        for (const childSection of section.childrenSections) {
            const childSections = getAllSectionAsArrayList(childSection)
            for (const childSection of childSections) {
                childrenSections.push(childSection)
            }
        }

        if (section.sectionId == "") { // i.e. when reached the root
            return childrenSections
        } else {
            return childrenSections.concat([section])
        }
    }
}

function checkExistenceOfAllElementsForSectionsAndMenuItems(section: Section) {
    let sideMenuHrefErrorMessages: string[] = []

    getAllSectionAsArrayList(section).forEach(section => {
        // console.log(`section.sectionId ${section.sectionId} section.menuItemId ${section.menuItemId}`)

        const sectionElement = document.getElementById(section.sectionId)
        if (!sectionElement) {
            throw new Error(`can't find element for ${section.sectionId}`)
        }

        const menuItemElement = document.getElementById(section.menuItemId)
        if (!menuItemElement) {
            throw new Error(`can't find element for ${section.menuItemId}`)
        }
        
        if (menuItemElement instanceof HTMLAnchorElement && new URL(menuItemElement.href).hash != `#${section.sectionId}`) {
            sideMenuHrefErrorMessages.push(`wrong href link in ${section.menuItemId}. got ${new URL(menuItemElement.href).hash} but should be #${section.sectionId}`)
        }
        
    })

    if (sideMenuHrefErrorMessages.length > 0) {
        throw new Error(sideMenuHrefErrorMessages.join("\n"))
    }
}

function getAllSectionElements(section: Section): Element[] {
    return getAllSectionAsArrayList(section).map(section => {
        const sectionElement = document.getElementById(section.sectionId)
        if (sectionElement) {
            return sectionElement
        } else {
            throw new Error(`can't find element for ${section.sectionId}`)
        }

    })
}

type MenuItemHightingGroups = [sectionsToHighlight: Section[], sectionsToUnhighlight: Section[]]

function findAllSectionsToHighlightOrUnhighlight(section: Section): MenuItemHightingGroups {
    if (section.childrenSections.length == 0) {
        if (section.isIntersecting) {
            return [[section], []]
        } else {
            return [[], [section]]
        }
    } else {
        let childrenSectionsToHighlight: Section[] = []
        let childrenSectionsToUnhighlight: Section[] = []

        for (const childSection of section.childrenSections) {
            const childSections = findAllSectionsToHighlightOrUnhighlight(childSection)

            for (const childSection of childSections[0]) {
                childrenSectionsToHighlight.push(childSection)
            }

            for (const childSection of childSections[1]) {
                childrenSectionsToUnhighlight.push(childSection)
            }
        }

        if (childrenSectionsToHighlight.length > 0) {
            return [childrenSectionsToHighlight.concat([section]), childrenSectionsToUnhighlight]
        } else {
            if (section.isIntersecting) {
                return [[section], childrenSectionsToUnhighlight]
            } else {
                return [[], childrenSectionsToUnhighlight.concat([section])]
            }
        }
    }
}

function getCriticalPathToSection(section: Section): Section[] {
    if (section.parentSection == undefined) {
        return [section]
    }
    return getCriticalPathToSection(section.parentSection).concat([section])
}


function printSectionsTree(section: Section, debth: number = 0) {

    let spacesToPrint = ""

    let i = 0
    while (i < debth) {
        spacesToPrint = spacesToPrint + "  "
        i++
    }

    // console.log(`section: ${spacesToPrint}${section.sectionId} with menu-item ${section.menuItemId}`);
    section.childrenSections.forEach(section => {
        printSectionsTree(section, debth + 2)
    })
}

function getAllDuplicateIds() {  
    // @ts-ignore
    const elements = [...document.querySelectorAll('[id]')];  
    const ids = elements.map(el => el.id);  
    const dups = elements.filter(el => ids.filter(id => id === el.id).length > 1);  
    
    return dups;  
}

// printSectionsTree(rootSection)
// console.log(getAllDuplicateIds())
// checkExistenceOfAllElementsForSectionsAndMenuItems(rootSection) // TODO: rethink this, not needed for runtime functionality but catches missing HTML ids



const allSections = getAllSectionAsArrayList(rootSection)
const allSectionElements = getAllSectionElements(rootSection)

document.addEventListener("DOMContentLoaded", function(event) {

    mixpanel.track('Page View')

    setupClicks()

    createScrollingVisibilityObserver(Array.from(allSectionElements), (entries, observer) => {

        entries.forEach(entry => {
            const entrySection = allSections.find(section => {
                if (section.sectionId == entry.target.id) {
                    return true
                } else {
                    return false
                }
            })
            if (entrySection) {
                entrySection.isIntersecting = entry.isIntersecting
                if (entry.rootBounds) {
                    entrySection.yRelativeToRoot = entry.intersectionRect.top
                }
            }
        })

        const rootRect = entries[0].rootBounds ?? DOMRectReadOnly.fromRect(new DOMRect())

        const findAllSectionsToHighlightOrUnhighlightResult = findAllSectionsToHighlightOrUnhighlight(rootSection)
        const highlightSections = findAllSectionsToHighlightOrUnhighlightResult[0]

        highlightSections.filter(section => { // NOTE: get only leaf nodes
            return section.childrenSections.length == 0
        }).forEach(section => {
            const rootYCenter = (rootRect.top + rootRect.bottom) / 2
            const distance = rootYCenter - section.yRelativeToRoot
            // console.log(`highlightSection leaf: ${section.sectionId} | ${section.yCenterRelativeToRoot} | ${distance}`);
        })
        
        allSections.forEach(section => {
            const sectionMenuItem = document.getElementById(section.menuItemId)
            if (sectionMenuItem) {
                sectionMenuItem.classList.remove('text-blue-400')
            }
        })

        if (highlightSections.length > 0) {
            showHoveringTableOfContents()

            const theOneLeafSectionToHighlight = highlightSections/*.filter(section => { // NOTE: get only leaf nodes
                return section.childrenSections.length == 0
            })*/.reduce((previousSection, currentSection) => { // NOTE: on a second thought, don't know why we need this.... 

                const previousSectionElementRect = document.getElementById(previousSection.sectionId)?.getBoundingClientRect()
                const currentSectionElementRect = document.getElementById(currentSection.sectionId)?.getBoundingClientRect()
                if (previousSectionElementRect && currentSectionElementRect) {

                    if (currentSectionElementRect.y < currentSectionElementRect.y) {
                        return currentSection
                    } else {
                        return previousSection
                    }

                } else {
                    return previousSection
                }
            })

            mixpanel.track('scrolled_to_section', {
                section_id: theOneLeafSectionToHighlight.sectionId
            })
    
            const criticalSectionPath = getCriticalPathToSection(theOneLeafSectionToHighlight)

            criticalSectionPath.forEach(section => {
                const sectionMenuItem = document.getElementById(section.menuItemId)
                if (sectionMenuItem) {
                    sectionMenuItem.scrollIntoView({ behavior: "auto", block: "nearest" }) // NOTE: behavior: "smooth" option doesn't work, likely because there are too many scrolling animations happening at the same time.
                    sectionMenuItem.classList.add('text-blue-400')
                }
            })
        } else {
            hideHoveringTableOfContents()
        }
        
    })
})

function createScrollingVisibilityObserver(elements: Element | Element[], callback: IntersectionObserverCallback) {
    const observer = new IntersectionObserver(callback, {
        root: null,
        threshold: [0.01, 0.02, 0.03, 0.04, 0.05, 0.06, 0.07, 0.08, 0.09, 0.1, 0.11, 0.12, 0.13, 0.14, 0.15, 0.16, 0.17, 0.18, 0.19, 0.2, 0.21, 0.22, 0.23, 0.24, 0.25, 0.26, 0.27, 0.28, 0.29, 0.3, 0.31, 0.32, 0.33, 0.34, 0.35, 0.36, 0.37, 0.38, 0.39, 0.4, 0.41, 0.42, 0.43, 0.44, 0.45, 0.46, 0.47, 0.48, 0.49, 0.5, 0.51, 0.52, 0.53, 0.54, 0.55, 0.56, 0.57, 0.58, 0.59, 0.6, 0.61, 0.62, 0.63, 0.64, 0.65, 0.66, 0.67, 0.68, 0.69, 0.7, 0.71, 0.72, 0.73, 0.74, 0.75, 0.76, 0.77, 0.78, 0.79, 0.8, 0.81, 0.82, 0.83, 0.84, 0.85, 0.86, 0.87, 0.88, 0.89, 0.9, 0.91, 0.92, 0.93, 0.94, 0.95, 0.96, 0.97, 0.98, 0.99, 1.0]
    })

    if (elements instanceof Element) {
        observer.observe(elements)
    } else {
        elements.forEach((element) => {
            observer.observe(element)
        })
    }
}

const sidebarTableOfContents = document.getElementById("sidebar-table-of-contents") as HTMLElement

function showHoveringTableOfContents() {
    sidebarTableOfContents.classList.add('transition-all', 'duration-1000', 'ease-in-out') 
    sidebarTableOfContents.classList.remove('opacity-0');
    sidebarTableOfContents.classList.add('opacity-100');
}

function hideHoveringTableOfContents() {
    sidebarTableOfContents.classList.remove('opacity-100');
    sidebarTableOfContents.classList.add('opacity-0');
}

function setupClicks() {
    mixpanel.track_links("a", "Click Link")
}